import { createSelector } from 'reselect'
import { LastMarkAllAsReadPreference } from 'shared/lib/store/actions/user'
import { UserPreference } from 'shared/src/store/actions/user'
import { FETCH_PREFERENCES_SUCCESS } from 'shared/store/actions/user/constants'
import { SectionNotificationSetting } from 'shared/typings/conversationsSidebar/Filters'
import Inbox from 'shared/typings/inboxes/Inbox'
import { Availability } from 'shared/typings/user/Availability'
import { User } from 'shared/typings/user/User'
import { UserStatus } from 'shared/typings/user/UserStatus'

import { generateConversationsDesktopAppConfig } from '@driftt/app-config'
import { DesktopAppConfigIF } from '@driftt/app-config/dist/utils/driftTypes'

import { RootState } from 'typesafe-actions'

const root = (state: RootState) => state.convo.user

export const getCurrentUser = (state: RootState): User | undefined =>
  state?.user?.gates && state?.user

export const getCurrentUserField = (field: keyof User) => (state: RootState) =>
  getCurrentUser(state)?.[field]

export const getCurrentUserId = createSelector(getCurrentUser, (user) => user?.id)

export const getUserProperty =
  (key: string) =>
  (state: RootState): any | undefined =>
    getCurrentUser(state)?.settings[key]

export const getUserProperties = (state: RootState): any | undefined =>
  getCurrentUser(state)?.settings

export const getUserRoles = (state: RootState): string[] | undefined => getCurrentUser(state)?.roles

export const HAS_DESKTOP_APP = 'has_desktop_app'

export const optedIntoDesktopApp = (state: RootState): boolean =>
  !!getUserProperty(HAS_DESKTOP_APP)(state) && getUserProperty(HAS_DESKTOP_APP)(state) !== 'false'

export const hasMobileApp = (state: RootState): boolean => !!getUserProperty('hasMobileApp')(state)

export const preferencesLoaded = (state: RootState): boolean =>
  root(state).currentUserPreferencesStatus === FETCH_PREFERENCES_SUCCESS

export const timeOptedIntoDesktopApp = (state: RootState): number =>
  optedIntoDesktopApp(state) && getUserProperty(HAS_DESKTOP_APP)(state)

export const getAbilitiesSelector = createSelector(getCurrentUser, (currentUser) => {
  const config = currentUser && getConfig(currentUser)
  const defaultAbilities = {
    hasAbilityToTag: false,
    hasEditableSavedReplies: false,
    hasSavedReplies: false,
    hasInviteTeammates: false,
    hasProspectingView: false,
    hasInternalNotes: false,
    hasLeaveConversation: false,
    hasOptOutOfNewExperience: false,
    hasAgentAvailability: false,
    hasNotificationSettings: false,
    hasPhoneToPhone: false,
    hasBrowserToPhone: false,
    hasChatToZoom: false,
    hasSearch: false,
    hasReplyToConversation: false,
    hasEditableEndUserAttributes: false,
    hasEditableTags: false,
    hasViewActivityTimeline: false,
    hasShowAllConversationsInSidebar: false,
    hasRemoveParticipantFromConversation: false,
    hasInstallWidget: false,
    hasCalendarRouting: false,
    hasInviteTeam: false,
    hasMultiParticipantMeetings: false,
    hasABMNotifications: false,
    hasConversationRating: false,
    hasRoutingAuditLog: false,
    hasConversationSections: false,
    hasMyConversations: false,
    hasNoParticipants: false,
    hasAllConversations: false,
    hasAdminNotificationSettings: false,
    hasAccountActivityNotifications: false,
    hasConvoNotifications: false,
    hasSendSMS: false,
  }
  if (!config) {
    return defaultAbilities
  }
  return {
    ...defaultAbilities,
    ...config,
  }
})

export const getCurrentUserAvailability = createSelector(
  getAbilitiesSelector,
  getCurrentUser,
  (abilities, user): Availability | undefined =>
    abilities.hasAgentAvailability ? user?.availability : Availability.AVAILABLE
)

export const hasCalendarRouting = (state: RootState): boolean => {
  return (
    !!getHasAbility('hasCalendarRouting')(state) &&
    !!getUserProperty('IS_CALENDAR_ROUTING_ENABLED')(state)
  )
}

export const getHasAbility =
  (ability: keyof DesktopAppConfigIF) =>
  (state: RootState): boolean => {
    const currentUser = getCurrentUser(state)
    const config = currentUser && getConfig(currentUser)

    return !config ? false : config[ability] ?? false
  }

export const getPreference =
  (preference: UserPreference) =>
  (state: RootState): any | undefined =>
    root(state).currentUserPreferences[preference]

export const getMarkAllAsReadPreference = (state: RootState): Record<number, number> => {
  const prefsArray = (root(state).currentUserPreferences.lastMarkAllAsRead ??
    []) as LastMarkAllAsReadPreference
  return prefsArray.reduce((acc: Record<number, number>, inbox) => {
    acc[inbox.inboxId] = inbox.timestamp
    return acc
  }, {})
}

export const getOrgId = (state: RootState): number | undefined => getCurrentUser(state)?.orgId

// This shouldn't need the typecast, there may be something wrong in app-config
const getConfig = (currentUser: User): DesktopAppConfigIF =>
  generateConversationsDesktopAppConfig(currentUser) as DesktopAppConfigIF

export const getOrgUsersById = (state: RootState): Record<number, User | undefined> =>
  root(state).byId

export const getActiveOrgUsers = createSelector(getOrgUsersById, (orgUsers): User[] =>
  Object.keys(orgUsers)
    .map((key) => orgUsers[Number(key)])
    .filter((user): user is User => !!user && user.status !== UserStatus.ARCHIVED && !user.bot)
    .map((user) => ({ ...user, name: user.name ?? user.email }))
)

export const byId = (state: RootState, userId: number): User | undefined => root(state).byId[userId]

export const getOrgUserById = (state: RootState, userId?: number): User | undefined => {
  if (!userId) {
    return undefined
  }
  return root(state).byId[userId]
}

export const getIsOrgUser = (state: RootState, id: number): boolean => {
  return id in root(state).byId
}

export const getIsBot = (state: RootState, id: number): boolean => {
  return getOrgUserById(state, id)?.bot ?? false
}

export const getCurrentUserIsBot = (state: RootState): boolean => {
  const currentUserId = getCurrentUserId(state) ?? 0
  return getIsBot(state, currentUserId)
}

export const updatedUserNotificationsBySubscription = (
  state: RootState
): Record<string, SectionNotificationSetting> => {
  return root(state).updatedUserNotificationsBySubscription
}

export const getCurrentInboxId = (state: RootState): number | undefined => {
  const activeInbox = getActiveInbox(state)
  return activeInbox?.id
}

export const getActiveInbox = (state: RootState): Inbox | null => {
  return root(state).activeInbox
}
